import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Users, MessageCircle, Map as MapIcon, Home, Menu, X, Layers, Radio } from 'lucide-react';
import Landing from './pages/Landing';
import Ecosystem from './pages/Ecosystem';
import EnhancedAgents from './pages/EnhancedAgents';
import Messages from './pages/Messages';
import Map from './pages/Map';
import './App.css';

// Hidden imports - routes still exist but are not in nav
import Dashboard from './pages/Dashboard';
import AddAgent from './pages/AddAgent';
import CloudOutageMap from './pages/CloudOutageMap';
import Deploy from './pages/Deploy';
import AgentChat from './pages/AgentChat';
import A2A from './pages/A2A';
import Join from './pages/Join';
import Network from './pages/Network';

function NavigationBar() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/ecosystem', label: 'Ecosystem', icon: Layers },
    { path: '/agents', label: 'Agents', icon: Users },
    { path: '/network', label: 'Network', icon: Radio },
    { path: '/messages', label: 'Messages', icon: MessageCircle },
    { path: '/map', label: 'Map', icon: MapIcon }
  ];

  const navLinkStyle = {
    padding: '6px 0',
    fontSize: '0.8125rem',
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.6)',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    borderBottom: '1px solid transparent'
  };

  return (
    <>
      <nav style={{
        background: '#000000',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        padding: isMobile ? '0 20px' : '0 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '56px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src="/nanda-logo-gold-white.svg"
            alt="Project NANDA"
            style={{ height: isMobile ? '28px' : '32px', objectFit: 'contain' }}
          />
        </Link>

        {/* Desktop Navigation */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            {navLinks.map(link => (
              <Link key={link.path} to={link.path} className="nav-link" style={navLinkStyle}>
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => navigate('/ecosystem')}
              style={{
                padding: '8px 16px',
                background: '#7a2020',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '0.8125rem',
                fontWeight: '400',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginLeft: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#5c1717'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#7a2020'}
            >
              Build on NEST
            </button>
          </div>
        )}

        {/* Mobile Hamburger */}
        {isMobile && (
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        )}
      </nav>

      {/* Mobile Sidebar */}
      {isMobile && (
        <>
          {isMenuOpen && (
            <div
              onClick={() => setIsMenuOpen(false)}
              style={{
                position: 'fixed',
                top: '56px',
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                zIndex: 999,
                transition: 'opacity 0.3s ease',
                opacity: isMenuOpen ? 1 : 0
              }}
            />
          )}

          <div style={{
            position: 'fixed',
            top: '56px',
            right: 0,
            bottom: 0,
            width: '280px',
            maxWidth: '80vw',
            background: '#000000',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease-in-out',
            zIndex: 1000,
            overflowY: 'auto',
            padding: '24px 0'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 20px' }}>
              {navLinks.map(link => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.path}
                    onClick={() => handleNavClick(link.path)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#ffffff',
                      padding: '16px 20px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <Icon size={18} style={{ color: '#7a2020' }} />
                    {link.label}
                  </button>
                );
              })}

              <div style={{ marginTop: '20px', padding: '0 20px' }}>
                <button
                  onClick={() => handleNavClick('/ecosystem')}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#7a2020',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    fontWeight: '400',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Build on NEST
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function App() {
  const [darkMode] = useState(true);

  return (
    <Router>
      <div className={`app ${darkMode ? 'dark' : 'light'}`} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <NavigationBar />

        <main style={{ flex: 1, overflow: 'auto', background: '#000000', paddingTop: '56px' }}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/ecosystem" element={<Ecosystem />} />
            <Route path="/agents" element={<EnhancedAgents />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/network" element={<Network />} />
            <Route path="/map" element={<Map />} />
            {/* Hidden routes - still accessible via URL but not in nav */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/agents/add" element={<AddAgent />} />
            <Route path="/cloudoutagemap" element={<CloudOutageMap />} />
            <Route path="/deploy" element={<Deploy />} />
            <Route path="/chat" element={<AgentChat />} />
            <Route path="/a2a" element={<A2A />} />
            <Route path="/join" element={<Join />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
